import { useState } from 'react'
import { TournamentRoundRobinView } from './TournamentRoundRobinView'
import { BracketTree } from './TournamentBracketTree'
import type { BracketMatch, BracketParticipant, GroupStagePlan } from './types'

interface TournamentGroupStageViewProps {
  plan: GroupStagePlan
  /** Result launching: called when a tappable match (group or knockout) is clicked. */
  onMatchTap?: (match: BracketMatch) => void
  /** Draft-only: make the group standings tappable so two teams can be swapped
   * (including between groups). See `swapGroupStageTeams`. */
  interactive?: boolean
  /** The team id currently picked for a swap (highlighted across all groups). */
  selectedTeamId?: string | null
  /** Called with a team id when its group standings row is tapped. */
  onTeamTap?: (teamId: string) => void
}

/**
 * Group stage → knockout, split into two tabs. **Grupos**: each group stacked
 * vertically, its standings beside its (navigable) round. **Mata-mata**: the
 * single-elimination tree whose slots reference the group placements. The
 * knockout tab only appears once it is generated. Read-only, tap-to-record via
 * `onMatchTap`, or tap-to-swap the group composition via `interactive`.
 */
export function TournamentGroupStageView({
  plan,
  onMatchTap,
  interactive = false,
  selectedTeamId = null,
  onTeamTap
}: TournamentGroupStageViewProps) {
  const hasKnockout = !!plan.knockout
  const [tab, setTab] = useState<'groups' | 'knockout'>('groups')
  const activeTab = hasKnockout ? tab : 'groups'

  return (
    <div className="h-full w-full flex flex-col">
      {hasKnockout && (
        <div className="flex-shrink-0 flex gap-1 mb-3 border-b border-gray-200">
          <PhaseTab active={activeTab === 'groups'} onClick={() => setTab('groups')}>
            Grupos
          </PhaseTab>
          <PhaseTab active={activeTab === 'knockout'} onClick={() => setTab('knockout')}>
            Mata-mata
          </PhaseTab>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto pb-2 pr-10">
        {activeTab === 'groups' ? (
          <div className="w-full">
            <p className="text-xs text-gray-500 mb-3">
              {plan.groups.length} grupos · classificam {plan.qualifiersPerGroup} por grupo
              {interactive && ' · toque em duas equipes para trocá-las de grupo'}
            </p>
            <div className="flex flex-col gap-8">
              {plan.groups.map((group) => (
                <div key={group.number}>
                  <div className="text-sm font-semibold text-gray-700 mb-2">{group.label}</div>
                  <TournamentRoundRobinView
                    plan={group.table}
                    onMatchTap={onMatchTap}
                    bare
                    interactive={interactive}
                    selectedTeamId={selectedTeamId}
                    onTeamTap={onTeamTap}
                    qualifiedTeamIds={group.qualifiedTeamIds}
                    disputingTeamIds={group.qualifyTie?.teams.map((t) => t.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          plan.knockout && (
            <div className="w-max min-w-full">
              <BracketTree
                plan={plan.knockout}
                interactive={false}
                selected={null}
                onTap={() => {}}
                scroll={false}
                onMatchTap={onMatchTap}
              />
            </div>
          )
        )}
      </div>
    </div>
  )
}

function PhaseTab({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`py-2 px-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
        active
          ? 'border-brand-primary text-brand-primary'
          : 'border-transparent text-gray-500 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * Swaps two teams across the group stage, relabeling every place each appears
 * (standings row + fixture matches). Because a group is a full round robin,
 * relabeling keeps a valid all-play-all fixture, so swapping identities is
 * exactly "move team A to B's spot and vice versa" — even between groups. Only
 * meaningful on a freshly drawn stage (before any result). No-op if either id is
 * absent or both are the same.
 */
export function swapGroupStageTeams(plan: GroupStagePlan, teamIdA: string, teamIdB: string): GroupStagePlan {
  if (teamIdA === teamIdB) return plan

  let teamA: BracketParticipant | null = null
  let teamB: BracketParticipant | null = null
  for (const group of plan.groups) {
    for (const row of group.table.standings) {
      if (row.team.id === teamIdA) teamA = row.team
      if (row.team.id === teamIdB) teamB = row.team
    }
  }
  if (!teamA || !teamB) return plan

  const relabel = (team: BracketParticipant): BracketParticipant => {
    if (team.id === teamIdA) return { ...teamB! }
    if (team.id === teamIdB) return { ...teamA! }
    return team
  }

  const clone = structuredClone(plan)
  for (const group of clone.groups) {
    group.table.standings = group.table.standings.map((row) => ({ ...row, team: relabel(row.team) }))
    group.table.rounds = group.table.rounds.map((round) => ({
      ...round,
      matches: round.matches.map((match) => ({
        ...match,
        top: { ...match.top, team: match.top.team ? relabel(match.top.team) : match.top.team },
        bottom: { ...match.bottom, team: match.bottom.team ? relabel(match.bottom.team) : match.bottom.team }
      }))
    }))
  }
  return clone
}
